import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { format, startOfDay, endOfDay } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import apiService from '../utils/apiService';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const SalesAnalytics = () => {
  const auth = useAuth();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(startOfDay(new Date()));
  const [endDate, setEndDate] = useState(endOfDay(new Date()));
  const [period, setPeriod] = useState('daily');
  const [userId, setUserId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [salesData, setSalesData] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userIncomes, setUserIncomes] = useState({});

  const fetchSalesData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get selected user's username if we have a userId
      const selectedUsername = userId ? users.find(u => u._id === userId)?.username : null;
  
      
      // Format dates as YYYY-MM-DD for the API
      const formatDateForAPI = (date) => {
        if (!date) return null;
        try {
          // Ensure we have a valid date object
          const dateObj = date instanceof Date ? date : new Date(date);
          if (isNaN(dateObj.getTime())) {
            console.error('Invalid date object:', date);
            return null;
          }
          return format(dateObj, 'yyyy-MM-dd');
        } catch (error) {
          console.error('Error formatting date:', error);
          return null;
        }
      };
      
      const fromDateStr = formatDateForAPI(startDate);
      const toDateStr = formatDateForAPI(endDate);
      
     
      
      if (!fromDateStr || !toDateStr) {
        throw new Error('Unable to format dates properly. Please check date selections.');
      }
      
      // Prepare request payload
      // Format dates with timezone consideration like Report.js does
      const apiStartDate = new Date(startDate);
      apiStartDate.setHours(0, 0, 0, 0);
      const apiEndDate = new Date(endDate);
      apiEndDate.setHours(23, 59, 59, 999);
      
      const payload = {
        fromDate: apiStartDate.toISOString(),
        toDate: apiEndDate.toISOString(),
        reportType: 'daily'
      };
      
      // For superadmins or agents, add branchId ONLY if explicitly selected.
      if ((auth?.user?.role === 'superadmin' || auth?.user?.role === 'agent') && branchId) {
        payload.branchId = branchId;
      }
      // For agents, if no branch is selected, the backend will handle fetching from all assigned branches.
      
      // Only add userId if it's selected
      if (userId) {
        payload.userId = userId;
      }
      
      
      
      const response = await apiService.post('reports/sales', payload);

      if (!response.ok) {
        // Try to get more detailed error information
        let errorMessage = 'Failed to fetch sales data';
        try {
          const errorData = await response.json();
          if (errorData && errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          console.error('Error parsing error response:', e);
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      
      // Show each report's income value and raw data
      data.forEach((report, index) => {
       
        
        if (report.rawReports && report.rawReports.length > 0) {
         
          report.rawReports.forEach((rawReport, rawIndex) => {
            
          });
        }
      });

      // Transform data for the bar chart
      const transformedData = {
        userIncome: [],
        totalIncome: 0
      };

      // Group sales by user
      const userSalesMap = new Map();


      
      data.forEach((report, index) => {
        // Extract user info and amount
        const reportUsername = report.user?.username || report.username || report.name;
        const amount = parseFloat(report.income || 0);

        if (index < 3) { // Only log first 3 reports to avoid spam
         
        }

        // Skip if we're filtering for a specific user and this isn't them
        if (selectedUsername && reportUsername !== selectedUsername) {
       
          return;
        }

        const currentTotal = userSalesMap.get(reportUsername) || 0;
        const newTotal = currentTotal + amount;
       
        
        userSalesMap.set(reportUsername, newTotal);
        transformedData.totalIncome += amount;
      });

      // Convert to array format for the chart
      transformedData.userIncome = Array.from(userSalesMap.entries()).map(([name, value]) => ({
        name,
        value: parseFloat(value) || 0
      }));

      // Sort by value in descending order
      transformedData.userIncome.sort((a, b) => b.value - a.value);

      // Update user incomes state
      const newUserIncomes = {};
      userSalesMap.forEach((value, username) => {
        newUserIncomes[username] = parseFloat(value) || 0;
      });
      setUserIncomes(newUserIncomes);

     
      setSalesData(transformedData);
    } catch (error) {
      console.error('Error fetching sales data:', error);
      setError('Failed to fetch sales data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Decide endpoint based on role and selected branch
      let endpoint = 'users';
      if (auth?.user?.role === 'agent' && branchId) {
        // Use dedicated route that enforces agent-branch access
        endpoint = `users/my-users?branchId=${encodeURIComponent(branchId)}`;
      }

      console.log('[SalesAnalytics] fetchUsers endpoint:', endpoint, 'branchId:', branchId, 'role:', auth?.user?.role);
      const response = await apiService.get(endpoint);
      console.log('[SalesAnalytics] fetchUsers response status:', response.status);
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      console.log('[SalesAnalytics] fetchUsers raw data length:', Array.isArray(data) ? data.length : 'N/A');

      // Normalize response list
      let filtered = Array.isArray(data) ? data : [];

      // If using general users endpoint, keep only end-users
      const usingMyUsers = auth?.user?.role === 'agent' && !!branchId;
      if (!usingMyUsers) {
        filtered = filtered.filter(u => (u.role || '').toLowerCase() === 'user');
      }

      // For superadmin, if a branch is selected, filter client-side
      if (auth?.user?.role === 'superadmin' && branchId) {
        filtered = filtered.filter(u => u.branchId === branchId);
      }

      const usersList = filtered.map(u => ({
        _id: u._id || u.id,
        username: u.username || u.name,
        branchId: u.branchId
      }));

      console.log('[SalesAnalytics] setting users count:', usersList.length, 'sample:', usersList[0]);
      setUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to fetch users: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await apiService.get('branches');
        
        if (!response.ok) {
          throw new Error('Failed to fetch branches');
        }
        
        const data = await response.json();
        setBranches(data);
      } catch (error) {
        console.error('Error fetching branches:', error);
      }
    };

    if (auth?.user?.role === 'superadmin') {
      fetchBranches();
    } else if (auth?.user?.role === 'agent') {
      const primaryBranch = auth.user.branch ? [{ id: auth.user.branch.id, name: auth.user.branch.name }] : [];
      const assignedBranches = auth.user.assignedBranches?.map(branch => ({
        id: branch.id,
        name: branch.name
      })) || [];
      
      const allBranches = [...primaryBranch, ...assignedBranches];
      const uniqueBranches = Array.from(new Map(allBranches.map(item => [item.id, item])).values());
      
      setBranches(uniqueBranches);
    }
  }, [auth]);

  useEffect(() => {
    if (auth) {
      // Reset currently selected user whenever branch changes
      setUserId('');
      setSelectedUser(null);
      fetchUsers();
    }
  }, [branchId, auth]);

  useEffect(() => {
    if (auth) {
      fetchSalesData();
    }
  }, [userId, startDate, endDate, period, branchId, auth]);

  // Only proceed if we have auth context
  if (!auth) {
    return (
      <Grid container spacing={2} justifyContent="center" alignItems="center">
        <Grid item>
          <CircularProgress />
        </Grid>
      </Grid>
    );
  }

  const { user } = auth;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <Grid container spacing={3}>
      {error && (
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      )}
      {loading ? (
        <Grid item xs={12} container justifyContent="center">
          <CircularProgress />
        </Grid>
      ) : (
        <>
          {/* Filters */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel>Period</InputLabel>
                    <Select
                      value={period}
                      onChange={(e) => setPeriod(e.target.value)}
                      label="Period"
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="yearly">Yearly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Select User</InputLabel>
                    <Select
                      value={userId || ''}
                      onChange={(e) => {
                        const newUserId = e.target.value;
                        
                        const selectedUser = users.find(u => u._id === newUserId);
                        
                        if (selectedUser) {
                         
                          setUserId(newUserId);
                          setSelectedUser(selectedUser);
                        } else if (newUserId === '') {
                          // Handle "All Users" selection
                          setUserId('');
                          setSelectedUser(null);
                        }
                      }}
                      label="Select User"
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            width: 250
                          }
                        }
                      }}
                    >
                      <MenuItem value="">
                        <em>All Users</em>
                      </MenuItem>
                      {users.map((user) => {
                       
                        if (!user._id) {
                          console.warn('User missing _id:', user);
                          return null;
                        }
                        return (
                          <MenuItem 
                            key={user._id} 
                            value={user._id}
                            sx={{
                              minHeight: '48px',
                              display: 'flex',
                              alignItems: 'center',
                              px: 2
                            }}
                          >
                            {user.username}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={setStartDate}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={setEndDate}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </LocalizationProvider>
                </Grid>
                {(user.role === 'superadmin' || (user.role === 'agent' && branches.length > 0)) && (
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <InputLabel>Branch</InputLabel>
                      <Select
                        value={branchId}
                        onChange={(e) => {
                          console.log('[SalesAnalytics] Branch changed to:', e.target.value);
                          setBranchId(e.target.value);
                        }}
                        label="Branch"
                      >
                        <MenuItem value="">All Branches</MenuItem>
                        {branches.map((branch) => (
                          <MenuItem key={branch.id} value={branch.id}>
                            {branch.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>

          {/* Summary Cards */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper
                  sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    height: 240,
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Income by User
                  </Typography>
                  {salesData?.userIncome?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={salesData.userIncome}
                        margin={{
                          top: 16,
                          right: 16,
                          bottom: 0,
                          left: 24,
                        }}
                      >
                        <XAxis
                          dataKey="name"
                          stroke={theme.palette.text.secondary}
                          style={{ fontSize: 12 }}
                        />
                        <YAxis
                          stroke={theme.palette.text.secondary}
                          style={{ fontSize: 12 }}
                        />
                        <Tooltip />
                        <Bar
                          dataKey="value"
                          fill={theme.palette.primary.main}
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography color="text.secondary" sx={{ mt: 2 }}>
                      No sales data available
                    </Typography>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper
                  sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    height: 240,
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Total Income
                  </Typography>
                  <Typography component="p" variant="h4">
                    {formatCurrency(salesData?.totalIncome || 0)}
                  </Typography>
                  {userId && (
                    <Typography color="text.secondary" sx={{ flex: 1 }}>
                      Showing sales for {users.find(u => u._id === userId)?.username}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Grid>

          {/* Sales Table */}
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Income</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salesData?.userIncome?.map((row) => (
                    <TableRow key={row.name}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{formatCurrency(row.value)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default SalesAnalytics;